import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { LogOut, Menu, Search, Shield, X, Sun, Moon, ChevronDown, Keyboard } from "lucide-react";
import { loadState, logout } from "../api";
import { nav, NAV_GROUPS, can } from "../nav";
import Module from "./Module";
import WelcomeOverlay from "./WelcomeOverlay";
import { useTheme } from "./Theme";
import { useVersion } from "./Version";
import { ActiveTeamProvider } from "./ActiveTeam";
import TeamSwitcher from "./TeamSwitcher";
import { useUnreadMessages } from "./useUnreadMessages";
import NotificationBell from "./NotificationBell";
import ChatFab from "./ChatFab";

const EMPTY_STATE = {
  members: [], groups: [], teams: [], matches: [], availability: [],
  squads: [], announcements: [], transactions: [], notifications: []
};

export default function Shell({ user, setUser }) {
  const [state, setState]   = useState(EMPTY_STATE);
  const [active, setActive] = useState("dashboard");
  // Optional payload carried alongside a navigation (e.g. a matchId
  // when jumping to a specific game's squad from the Dashboard).
  const [navArg, setNavArg] = useState(null);
  const [open, setOpen]     = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const { theme, toggleTheme } = useTheme();
  const { version, setVersion } = useVersion();

  // Views receive `setActive` as their `navigate` prop; wrap it so
  // callers can pass an optional payload with a route change.
  function navigate(key, arg = null) {
    setActive(key);
    setNavArg(arg);
  }
  // Global unread DM counter + toast on new arrival.
  const unreadMessages = useUnreadMessages({ active, onNavigate: setActive });

  // Phase 3 §3 — Welcome cinematic only fires once per logged-in session.
  // Login sets sessionStorage.gabruz-just-logged-in = "1"; Shell consumes &
  // clears it on mount so a page refresh while logged in doesn't replay.
  const [showWelcome, setShowWelcome] = useState(
    () => sessionStorage.getItem("gabruz-just-logged-in") === "1"
  );
  useEffect(() => {
    if (showWelcome) sessionStorage.removeItem("gabruz-just-logged-in");
  }, [showWelcome]);

  const reload = useCallback(async () => {
    setError("");
    try {
      const next = await loadState();
      setState(next);
    } catch (err) {
      // If our token expired, api.js already cleared it — bounce to login
      if (err.status === 401) { setUser(null); return; }
      setError(err.message || "Failed to load data");
    }
  }, [setUser]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      await reload();
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [reload]);

  const allowed = nav.filter(n => can(user, n.key));

  // Phase 3 — grouped nav. Items collected by group key, then we render
  // only the groups that have at least one item the user can see.
  const groupedNav = useMemo(() => {
    return NAV_GROUPS
      .map(g => ({ ...g, items: allowed.filter(n => n.group === g.key) }))
      .filter(g => g.items.length > 0);
  }, [allowed]);

  // Collapsible state per group — persisted to localStorage so the user's
  // collapsed/expanded preference survives reloads. Players typically only
  // want "My Stuff" + "Team" expanded.
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try {
      const raw = localStorage.getItem("gabruz-nav-collapsed");
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch { return new Set(); }
  });
  function toggleGroup(key) {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      try { localStorage.setItem("gabruz-nav-collapsed", JSON.stringify([...next])); } catch {}
      return next;
    });
  }

  function signOut() {
    logout();
    setUser(null);
  }

  // Phase 5 #5 — lock body scroll while the mobile drawer is open so the
  // page underneath doesn't move when the user swipes on the drawer.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Phase 5 #5 — Escape closes the drawer (matches the rest of the app's
  // Esc-closes-modal convention).
  useEffect(() => {
    if (!open) return;
    const onKey = e => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Phase 5 #11 — global keyboard shortcuts.
  //   /        focus the topbar search input
  //   ?        toggle the shortcut help overlay
  //   Esc      close help overlay (also handled by individual modals)
  //   g <key>  navigate to a section (g d → dashboard, g s → schedule, …)
  //
  // Shortcuts only fire when the user isn't typing in an input/textarea
  // (Ctrl/Cmd combos still pass through to the browser).
  const searchRef = useRef(null);
  const [showShortcuts, setShowShortcuts] = useState(false);
  // Track the "g" prefix for two-key nav combos. setTimeout clears it
  // after 1s so a stray g doesn't permanently swallow the next press.
  const gPrefixRef = useRef(null);
  useEffect(() => {
    function isTyping(target) {
      if (!target) return false;
      const tag = (target.tagName || "").toLowerCase();
      return tag === "input" || tag === "textarea" || tag === "select" || target.isContentEditable;
    }
    const NAV_MAP = {
      d: "dashboard", m: "members", g: "groups", t: "teams",
      s: "schedule",  a: "availability", q: "squad", p: "practice",
      r: "rankings", n: "announcements", f: "finance", v: "profile"
    };
    function onKey(e) {
      // Don't trap modifier combos — let Ctrl+F etc. through.
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const typing = isTyping(e.target);

      // ? — toggle help overlay. Pressing Esc closes it.
      if (e.key === "?" && !typing) {
        e.preventDefault();
        setShowShortcuts(s => !s);
        return;
      }
      if (e.key === "Escape" && showShortcuts) {
        setShowShortcuts(false);
        return;
      }

      if (typing) return;

      // / — focus the topbar search input
      if (e.key === "/") {
        e.preventDefault();
        searchRef.current?.focus();
        return;
      }

      // g <letter> — navigate to a section
      if (gPrefixRef.current) {
        const target = NAV_MAP[e.key.toLowerCase()];
        clearTimeout(gPrefixRef.current);
        gPrefixRef.current = null;
        if (target) {
          e.preventDefault();
          setActive(target);
        }
        return;
      }
      if (e.key === "g") {
        gPrefixRef.current = setTimeout(() => { gPrefixRef.current = null; }, 1000);
        return;
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showShortcuts]);

  return (
    <ActiveTeamProvider user={user} teams={state.teams}>
    <div className={`app${showWelcome ? " app--welcoming" : ""}`}>
      {showWelcome && <WelcomeOverlay onDone={() => setShowWelcome(false)} />}
      {/* §29 — main content comes first in DOM so the sidebar (rendered after)
          lands in the right-hand grid column without any explicit ordering. */}
      <main>
        <header className="topbar">
          <button className="icon mobile" onClick={() => setOpen(true)}><Menu /></button>
          <NotificationBell notifications={state.notifications} onNavigate={setActive}/>
          <div className="titleblock">
            <p>Gabruz Universe Platform</p>
            <h1>{nav.find(n => n.key === active)?.label}</h1>
          </div>
          <div className="search">
            <Search size={17} />
            <input
              ref={searchRef}
              placeholder="Search anything…  (press /)"
            />
          </div>
          <div className="rolepill">
            <Shield size={16} />{user.roles[0].replace("_", " ")}
          </div>
          {/* Phase 3 — global active-team switcher (drives every team-scoped page). */}
          <TeamSwitcher />
          {/* Phase 3 — design version switcher (V1 / V2 / V3). */}
          <div className="versionSwitch" role="radiogroup" aria-label="UI design version">
            <button type="button" className={version === "v1" ? "versionSwitch-btn versionSwitch-btn--active" : "versionSwitch-btn"} onClick={() => setVersion("v1")} role="radio" aria-checked={version === "v1"} title="Original UI">V1</button>
            <button type="button" className={version === "v2" ? "versionSwitch-btn versionSwitch-btn--active" : "versionSwitch-btn"} onClick={() => setVersion("v2")} role="radio" aria-checked={version === "v2"} title="Aurora UI">V2</button>
            <button type="button" className={version === "v3" ? "versionSwitch-btn versionSwitch-btn--active" : "versionSwitch-btn"} onClick={() => setVersion("v3")} role="radio" aria-checked={version === "v3"} title="Bento UI">V3</button>
            <button type="button" className={version === "v4" ? "versionSwitch-btn versionSwitch-btn--active" : "versionSwitch-btn"} onClick={() => setVersion("v4")} role="radio" aria-checked={version === "v4"} title="Mobile UI">V4</button>
          </div>
          {/* Phase 3 §1 — theme toggle. */}
          <button
            type="button"
            className="themeToggle"
            onClick={toggleTheme}
            title={theme === "light" ? "Switch to dark theme" : "Switch to light theme"}
            aria-label="Toggle color theme"
          >
            {theme === "light" ? <Moon size={18} /> : <Sun size={18} />}
          </button>
          {/* Phase 5 #11 — keyboard shortcut help. Press ? anywhere too. */}
          <button
            type="button"
            className="themeToggle"
            onClick={() => setShowShortcuts(true)}
            title="Keyboard shortcuts (press ?)"
            aria-label="Show keyboard shortcuts"
          >
            <Keyboard size={18} />
          </button>
        </header>

        <div className="page">
          {loading ? (
            <div className="empty">
              <h2>Loading…</h2>
              <p>Fetching data from the API.</p>
            </div>
          ) : error ? (
            <div className="empty">
              <h2>Couldn't load data</h2>
              <p>{error}</p>
              <button className="primary" onClick={reload}>Retry</button>
            </div>
          ) : (
            <Module active={active} setActive={navigate} navArg={navArg} state={state} reload={reload} user={user} setUser={setUser} />
          )}
        </div>
      </main>

      {/* Phase 5 #5 — clickable backdrop behind the mobile drawer */}
      {open && (
        <div
          className="drawerBackdrop"
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />
      )}
      <aside className={open ? "sidebar open" : "sidebar"}>
        <div className="brand">
          <img src="/logo.png" onError={e => e.currentTarget.style.display = "none"} />
          <div>
            <h2>Gabruz</h2>
            <span>Universe</span>
          </div>
          <button className="icon mobile" onClick={() => setOpen(false)}><X /></button>
        </div>

        <button
          type="button"
          className="profile profile-btn"
          onClick={() => { setActive("profile"); setOpen(false); }}
          title="Open my profile"
        >
          <div className="avatar">{user.avatar}</div>
          <div>
            <strong>{user.name}</strong>
            <span>{user.title}</span>
          </div>
        </button>

        <div className="nav-section">
          {groupedNav.map(group => {
            const collapsed = collapsedGroups.has(group.key);
            return (
              <div key={group.key} className="navGroup">
                <button
                  type="button"
                  className="navGroup-head"
                  onClick={() => toggleGroup(group.key)}
                  aria-expanded={!collapsed}
                >
                  <ChevronDown
                    size={14}
                    className={collapsed ? "navGroup-chev navGroup-chev--collapsed" : "navGroup-chev"}
                  />
                  <span>{group.label}</span>
                </button>
                {!collapsed && group.items.map(item => {
                  const Icon = item.icon;
                  const badge = item.key === "messages" && unreadMessages > 0
                    ? unreadMessages : null;
                  return (
                    <button
                      key={item.key}
                      className={active === item.key ? "nav active" : "nav"}
                      onClick={() => { setActive(item.key); setOpen(false); }}
                    >
                      <Icon size={18} />
                      <span>{item.label}</span>
                      {badge && (
                        <em className="nav-badge" aria-label={`${badge} unread messages`}>
                          {badge > 99 ? "99+" : badge}
                        </em>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>

        <button className="logout" onClick={signOut}>
          <LogOut size={18} /> Sign Out
        </button>
      </aside>

      <ChatFab active={active} unread={unreadMessages} onOpen={() => setActive("messages")} />

      {/* Phase 5 #11 — Keyboard shortcuts overlay */}
      {showShortcuts && (
        <div className="modal" onClick={() => setShowShortcuts(false)}>
          <div className="modalInner shortcutModal" onClick={e => e.stopPropagation()}>
            <button className="close" onClick={() => setShowShortcuts(false)} title="Close"><X size={20}/></button>
            <h2 className="shortcutTitle"><Keyboard size={20}/> Keyboard Shortcuts</h2>
            <div className="shortcutGrid">
              <div className="shortcutCol">
                <h4>Global</h4>
                <dl>
                  <dt><kbd>/</kbd></dt><dd>Focus the search bar</dd>
                  <dt><kbd>?</kbd></dt><dd>Show this help</dd>
                  <dt><kbd>Esc</kbd></dt><dd>Close any modal or menu</dd>
                </dl>
              </div>
              <div className="shortcutCol">
                <h4>Navigate <small>(press <kbd>g</kbd> then…)</small></h4>
                <dl>
                  <dt><kbd>g</kbd> <kbd>d</kbd></dt><dd>Dashboard</dd>
                  <dt><kbd>g</kbd> <kbd>m</kbd></dt><dd>Members</dd>
                  <dt><kbd>g</kbd> <kbd>g</kbd></dt><dd>Groups</dd>
                  <dt><kbd>g</kbd> <kbd>t</kbd></dt><dd>Teams</dd>
                  <dt><kbd>g</kbd> <kbd>s</kbd></dt><dd>Schedule</dd>
                  <dt><kbd>g</kbd> <kbd>a</kbd></dt><dd>Availability</dd>
                  <dt><kbd>g</kbd> <kbd>q</kbd></dt><dd>Squads</dd>
                  <dt><kbd>g</kbd> <kbd>p</kbd></dt><dd>Practice</dd>
                  <dt><kbd>g</kbd> <kbd>r</kbd></dt><dd>Rankings</dd>
                  <dt><kbd>g</kbd> <kbd>n</kbd></dt><dd>Announcements</dd>
                  <dt><kbd>g</kbd> <kbd>f</kbd></dt><dd>Finance</dd>
                  <dt><kbd>g</kbd> <kbd>v</kbd></dt><dd>My Profile</dd>
                </dl>
              </div>
            </div>
            <small className="shortcutFoot">
              Shortcuts pause while you're typing in an input. Ctrl/Cmd combos pass through to the browser.
            </small>
          </div>
        </div>
      )}
    </div>
    </ActiveTeamProvider>
  );
}
